import { lazy, Suspense, useEffect, useState } from 'react'
import { Link, Navigate, useLocation, useParams } from 'react-router-dom'
import { useAuth } from '../auth'
import { Topbar } from '../components/Topbar'
import { FEATURES } from 'virtual:features'
import { PluginMetaProvider, type PluginMeta } from '../hooks/usePluginMeta'

// Build a map of pluginId → lazy component
const LOADERS = import.meta.glob<{ default: React.ComponentType }>('../features/*/Page.tsx')

const componentCache: Record<string, React.LazyExoticComponent<React.ComponentType>> = {}

function getComponent(pluginId: string) {
  if (componentCache[pluginId]) return componentCache[pluginId]
  const entry = Object.entries(LOADERS).find(([path]) =>
    path.includes(`/features/${pluginId}/Page.tsx`),
  )
  if (!entry) return null
  const [, loader] = entry
  componentCache[pluginId] = lazy(loader)
  return componentCache[pluginId]
}

export function PluginHost() {
  const { pluginId } = useParams<{ pluginId: string }>()
  const { features } = useAuth()
  const location = useLocation()
  const [tick, setTick] = useState(0)

  // Force re-mount on inner-route change so plugin components can react to navigation.
  useEffect(() => {
    setTick((t) => t + 1)
  }, [location.pathname])

  if (!pluginId) return <Navigate to="/" replace />
  const meta = features.find((f) => f.pluginId === pluginId)
  if (!meta) return <Navigate to="/" replace />

  const Component = getComponent(pluginId)
  const known = FEATURES.find((f) => f.id === pluginId)
  const pluginMeta: PluginMeta = {
    ...meta,
    selfManageChrome: known?.selfManageChrome === true,
  }
  const chromeOwn = pluginMeta.selfManageChrome

  return (
    <div className="page">
      {/* Topbar always renders — pages rely on it for ← Home / username / Admin,
       * so we don't want to duplicate that chrome per-page. selfManageChrome
       * pages just suppress the gap below (see .plugin-host-main--chrome). */}
      <Topbar
        backTo="/"
        backLabel="← Home"
        centerSlot={
          <span className="topbar-plugin-title">
            {meta.icon && <span className="topbar-plugin-icon" aria-hidden="true">{meta.icon}</span>}
            <span className="topbar-plugin-name">{meta.name}</span>
            <span className="topbar-plugin-id">{meta.pluginId}</span>
          </span>
        }
      />
      <main className={chromeOwn ? 'plugin-host-main plugin-host-main--chrome' : 'plugin-host-main'}>
        {Component ? (
          <PluginMetaProvider value={pluginMeta}>
            <Suspense fallback={<div className="muted">Loading…</div>}>
              <div key={tick}>
                <Component />
              </div>
            </Suspense>
          </PluginMetaProvider>
        ) : (
          <div className="plugin-host-error">
            Plugin frontend not found
            {known ? '' : ' — manifest missing for this plugin_id'}.
            <br />
            <span className="muted">
              Expected{' '}
              <code>web/src/features/{pluginId}/Page.tsx</code>
            </span>
          </div>
        )}

        {chromeOwn ? null : (
          <div className="plugin-host-foot">
            <Link to="/" className="topbar-back">
              ← Back to home
            </Link>
          </div>
        )}
      </main>
    </div>
  )
}
